import { toast } from 'react-toastify';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Settings, ChevronUp, ChevronDown, Pencil, Trash2, GripVertical } from 'lucide-react';
import Pagination from 'react-responsive-pagination';
import 'react-responsive-pagination/themes/classic.css';
import AppLayout from '../components/AppLayout';
import Drawer from '../components/Drawer';
import OptionFormSlider from '../components/OptionFormSlider';
import OptionDetailsSlider from '../components/OptionDetailsSlider';
import AddCategoryModal from '../components/AddCategoryModal';
import api from '../services/api';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';

interface Option {
  _id: string;
  name: string;
  description?: string;
  caption?: string;
  projectId: string;
  projectName?: string;
  categoryId?: string;
  categoryName?: string;
  location?: string;
  phase?: string;
  isActive: boolean;
  isValid: boolean;
  order: number;
  heroImageId?: string;
  heroImageUrl?: string;
  spaces?: string[];
  createdAt: string;
  updatedAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Project {
  _id: string;
  name: string;
}

interface Category {
  _id: string;
  name: string;
  description?: string;
  projectId: string;
  projectName?: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const Options: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab state
  const [activeTab, setActiveTab] = useState<'options' | 'categories'>('options');

  // Options state
  const [options, setOptions] = useState<Option[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Categories state
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState('');
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [categoryProjectFilter, setCategoryProjectFilter] = useState('');
  const [categoryPagination, setCategoryPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Form loading state
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    if (workspaceId) {
      fetchProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  // Set first project as default when projects are loaded
  useEffect(() => {
    if (projects.length > 0 && !projectFilter) {
      setProjectFilter(projects[0]._id);
    }
    if (projects.length > 0 && !categoryProjectFilter) {
      setCategoryProjectFilter(projects[0]._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  useEffect(() => {
    if (workspaceId && activeTab === 'options' && projectFilter) {
      fetchOptions();
    } else if (workspaceId && activeTab === 'categories' && categoryProjectFilter) {
      fetchCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, activeTab, pagination.page, pagination.limit, searchQuery, projectFilter, categoryPagination.page, categoryPagination.limit, categorySearchQuery, categoryProjectFilter]);

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchOptions = async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      setError('');

      const response = await api.get(`/workspaces/${workspaceId}/pkg-options`, {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchQuery,
          projectId: projectFilter || undefined,
          sortBy: 'order',
          sortOrder: 'asc'
        }
      });

      setOptions(response.data.options);
      setPagination(response.data.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch options');
      console.error('Error fetching options:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    if (!workspaceId) return;

    try {
      setCategoriesLoading(true);
      setCategoriesError('');

      const response = await api.get(`/workspaces/${workspaceId}/pkg-categories`, {
        params: {
          page: categoryPagination.page,
          limit: categoryPagination.limit,
          search: categorySearchQuery,
          projectId: categoryProjectFilter || undefined,
          sortBy: 'order',
          sortOrder: 'asc'
        }
      });

      setCategories(response.data.categories);
      setCategoryPagination(response.data.pagination);
    } catch (err: any) {
      setCategoriesError(err.response?.data?.message || 'Failed to fetch categories');
      console.error('Error fetching categories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleProjectFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProjectFilter(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    // Reset to first project instead of empty
    if (projects.length > 0) {
      setProjectFilter(projects[0]._id);
    }
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleLimitChange = (limit: number) => {
    setPagination({ ...pagination, limit, page: 1 }); // Reset to page 1 when changing limit
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder options array
    const newOptions = [...options];
    const [draggedOption] = newOptions.splice(draggedIndex, 1);
    newOptions.splice(dropIndex, 0, draggedOption);

    // Update local state immediately for better UX
    setOptions(newOptions);
    setDraggedIndex(null);
    setDragOverIndex(null);

    // Update order in backend
    try {
      await Promise.all(
        newOptions.map((option, index) =>
          api.put(
            `/workspaces/${workspaceId}/pkg-options/${option._id}`,
            { order: index }
          )
        )
      );
    } catch (err: any) {
      console.error('Error updating option order:', err);
      // Revert on error by refetching
      fetchOptions();
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleCreateOption = () => {
    setSearchParams({ action: 'new' });
  };

  const handleViewOption = (id: string) => {
    setSearchParams({ view: id });
  };

  const handleCloseDrawer = () => {
    setSearchParams({});
    fetchOptions();
  };

  const handleCloseSlider = () => {
    setSearchParams({});
  };

  const handleSaveOption = () => {
    fetchOptions();
  };

  // Category handlers
  const handleCreateCategory = () => {
    setEditingCategory(null);
    setShowAddCategoryModal(true);
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setShowAddCategoryModal(true);
  };

  const handleDeleteCategory = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      await api.delete(`/workspaces/${workspaceId}/pkg-categories/${id}`);
      fetchCategories();
    } catch (err: any) {
      console.error('Error deleting category:', err);
      toast.error(err.response?.data?.message || 'Failed to delete category');
    }
  };

  const handleReorderCategory = async (id: string, direction: 'up' | 'down') => {
    try {
      await api.put(
        `/workspaces/${workspaceId}/pkg-categories/${id}/reorder`,
        { direction }
      );
      fetchCategories();
    } catch (err: any) {
      console.error('Error reordering category:', err);
      toast.error(err.response?.data?.message || 'Failed to reorder category');
    }
  };

  const handleCategorySearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCategorySearchQuery(e.target.value);
    setCategoryPagination({ ...categoryPagination, page: 1 });
  };

  const handleCategoryProjectFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCategoryProjectFilter(e.target.value);
    setCategoryPagination({ ...categoryPagination, page: 1 });
  };

  const handleCategoryClearFilters = () => {
    setCategorySearchQuery('');
    // Reset to first project instead of empty
    if (projects.length > 0) {
      setCategoryProjectFilter(projects[0]._id);
    }
    setCategoryPagination({ ...categoryPagination, page: 1 });
  };

  const handleCategoryPageChange = (page: number) => {
    setCategoryPagination({ ...categoryPagination, page });
  };

  const handleCategoryLimitChange = (limit: number) => {
    setCategoryPagination({ ...categoryPagination, limit, page: 1 }); // Reset to page 1 when changing limit
  };

  const handleCategoryModalClose = () => {
    setShowAddCategoryModal(false);
    setEditingCategory(null);
  };

  const handleCategoryModalSave = () => {
    setShowAddCategoryModal(false);
    setEditingCategory(null);
    fetchCategories();
  };

  const isDrawerOpen = searchParams.get('action') === 'new';
  const viewOptionId = searchParams.get('view');

  if (!workspaceId) {
    return (
      <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
        <div className="builders-container">
          <div className="builders-error">
            <p>No workspace selected</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <>
        <div className="builders-container">
          {/* Section Header */}
          <div className="builders-section-header">
            <div>
              <h2 className="builders-section-title">Options</h2>
              <p className="builders-section-subtitle">
                Manage options and their configurations
              </p>
            </div>
          </div>

          {/* White Card Container */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)'
          }}>
            {/* Tabs */}
            <div style={{
              display: 'flex',
              gap: '10px',
              marginBottom: '20px',
              backgroundColor: '#D4CBC4',
              padding: '10px',
              borderRadius: '8px'
            }}>
              <button
                onClick={() => setActiveTab('options')}
                style={{
                  padding: '10px 30px',
                  borderRadius: '20px',
                  border: 'none',
                  backgroundColor: activeTab === 'options' ? '#48182F' : 'transparent',
                  color: activeTab === 'options' ? 'white' : '#6B7280',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Options
              </button>
              <button
                onClick={() => setActiveTab('categories')}
                style={{
                  padding: '10px 30px',
                  borderRadius: '20px',
                  border: 'none',
                  backgroundColor: activeTab === 'categories' ? '#48182F' : 'transparent',
                  color: activeTab === 'categories' ? 'white' : '#6B7280',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Categories
              </button>
            </div>

            {/* Options Tab Content */}
            {activeTab === 'options' && (
              <>
                {/* Options Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginBottom: '20px' }}>
                  <button
                    className="btn btn-outline btn-md"
                    onClick={handleClearFilters}
                  >
                    Clear
                  </button>
                  <button className="btn btn-primary btn-md" onClick={handleCreateOption}>
                    + Add New Option
                  </button>
                </div>

                {/* Filters */}
                <div className="builders-actions" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '20px' }}>
                  <select
                    className="form-input"
                    value={projectFilter}
                    onChange={handleProjectFilterChange}
                  >
                    {projects.map(project => (
                      <option key={project._id} value={project._id}>{project.name}</option>
                    ))}
                  </select>

                  <input
                    type="text"
                    className="form-input"
                    placeholder="Search options..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </div>

                {/* Error Display */}
                {error && (
                  <div className="builders-error" style={{ marginBottom: '20px' }}>
                    <p>{error}</p>
                  </div>
                )}
              </>
            )}

            {/* Categories Tab Content */}
            {activeTab === 'categories' && (
              <>
                {/* Categories Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginBottom: '20px' }}>
                  <button
                    className="btn btn-outline btn-md"
                    onClick={handleCategoryClearFilters}
                  >
                    Clear
                  </button>
                  <button className="btn btn-primary btn-md" onClick={handleCreateCategory}>
                    + Add New Category
                  </button>
                </div>

                {/* Filters */}
                <div className="builders-actions" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '20px' }}>
                  <select
                    className="form-input"
                    value={categoryProjectFilter}
                    onChange={handleCategoryProjectFilterChange}
                  >
                    {projects.map(project => (
                      <option key={project._id} value={project._id}>{project.name}</option>
                    ))}
                  </select>

                  <input
                    type="text"
                    className="form-input"
                    placeholder="Search categories..."
                    value={categorySearchQuery}
                    onChange={handleCategorySearchChange}
                  />
                </div>

                {/* Error Display */}
                {categoriesError && (
                  <div className="builders-error" style={{ marginBottom: '20px' }}>
                    <p>{categoriesError}</p>
                  </div>
                )}
              </>
            )}

            {/* Loading State */}
            {(activeTab === 'options' && loading) || (activeTab === 'categories' && categoriesLoading) ? (
              <div className="builders-loading">
                <div className="builders-loading-spinner" />
              </div>
            ) : (
              <>
                {/* Options Table */}
                {activeTab === 'options' && (
                  <>
                    <div className="data-table-container">
                      <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}></th>
                        <th>Image</th>
                        <th>Project</th>
                        <th>Name</th>
                        <th>Category</th>
                        <th>Location</th>
                        <th>Phase</th>
                        <th>Order</th>
                        <th>Status</th>
                        <th>Valid</th>
                        <th>Spaces</th>
                        <th>Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {options.length === 0 ? (
                        <tr>
                          <td colSpan={12} className="data-table-empty">
                            No options found. Click "+ Add New Option" to create one.
                          </td>
                        </tr>
                      ) : (
                        options.map((option, index) => (
                          <tr
                            key={option._id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            style={{
                              opacity: draggedIndex === index ? 0.4 : 1,
                              backgroundColor: dragOverIndex === index ? '#F3F4F6' : 'transparent',
                              cursor: 'move'
                            }}
                          >
                            <td style={{ cursor: 'grab', padding: '8px' }}>
                              <GripVertical size={20} color="#9CA3AF" />
                            </td>
                            <td onClick={() => handleViewOption(option._id)} style={{ cursor: 'pointer' }}>
                              {option.heroImageUrl ? (
                                <img
                                  src={option.heroImageUrl}
                                  alt={option.name}
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                    border: '1px solid #E5E7EB'
                                  }}
                                />
                              ) : (
                                <div style={{ width: '40px', height: '40px', backgroundColor: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>
                                  <Settings size={24} color="#6B7280" />
                                </div>
                              )}
                            </td>
                            <td onClick={() => handleViewOption(option._id)} style={{ cursor: 'pointer' }} className="data-table-primary-cell">{option.projectName || '-'}</td>
                            <td onClick={() => handleViewOption(option._id)} style={{ cursor: 'pointer' }} className="data-table-primary-cell">{option.name}</td>
                            <td onClick={() => handleViewOption(option._id)} style={{ cursor: 'pointer' }}>{option.categoryName || '-'}</td>
                            <td onClick={() => handleViewOption(option._id)} style={{ cursor: 'pointer' }}>{option.location || '-'}</td>
                            <td onClick={() => handleViewOption(option._id)} style={{ cursor: 'pointer' }}>{option.phase || '-'}</td>
                            <td onClick={() => handleViewOption(option._id)} style={{ cursor: 'pointer' }}>{option.order}</td>
                            <td onClick={() => handleViewOption(option._id)} style={{ cursor: 'pointer' }}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                backgroundColor: option.isActive ? '#DEF7EC' : '#FEE2E2',
                                color: option.isActive ? '#03543F' : '#991B1B'
                              }}>
                                {option.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td onClick={() => handleViewOption(option._id)} style={{ cursor: 'pointer' }}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                backgroundColor: option.isValid ? '#DBEAFE' : '#FEF3C7',
                                color: option.isValid ? '#1E40AF' : '#92400E'
                              }}>
                                {option.isValid ? 'Valid' : 'Invalid'}
                              </span>
                            </td>
                            <td onClick={() => handleViewOption(option._id)} style={{ cursor: 'pointer' }}>{option.spaces?.length || 0}</td>
                            <td onClick={() => handleViewOption(option._id)} style={{ cursor: 'pointer' }}>{new Date(option.updatedAt).toLocaleDateString()}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="contacts-pagination-controls">
                  {/* Records Per Page Selector */}
                  <div className="contacts-records-per-page">
                    <span>Show</span>
                    <select
                      value={pagination.limit}
                      onChange={(e) => handleLimitChange(Number(e.target.value))}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span>records per page (Total: {pagination.total})</span>
                  </div>

                  {/* Pagination */}
                  {pagination.pages > 1 && (
                    <div className="contacts-pagination-wrapper">
                      <Pagination
                        current={pagination.page}
                        total={pagination.pages}
                        onPageChange={handlePageChange}
                        maxWidth={400}
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Categories Table */}
            {activeTab === 'categories' && (
              <>
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Name</th>
                        <th>Order</th>
                        <th>Active</th>
                        <th style={{ width: '150px', textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categories.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="data-table-empty">
                            No categories found. Click "+ Add New Category" to create one.
                          </td>
                        </tr>
                      ) : (
                        categories.map((category) => (
                          <tr key={category._id}>
                            <td className="data-table-primary-cell">{category.projectName || '-'}</td>
                            <td className="data-table-primary-cell">{category.name}</td>
                            <td>{category.order}</td>
                            <td>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                backgroundColor: category.isActive ? '#DEF7EC' : '#FEE2E2',
                                color: category.isActive ? '#03543F' : '#991B1B'
                              }}>
                                {category.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReorderCategory(category._id, 'up');
                                  }}
                                  className="btn btn-icon btn-sm"
                                  title="Move up"
                                  style={{ padding: '4px' }}
                                >
                                  <ChevronUp size={16} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReorderCategory(category._id, 'down');
                                  }}
                                  className="btn btn-icon btn-sm"
                                  title="Move down"
                                  style={{ padding: '4px' }}
                                >
                                  <ChevronDown size={16} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditCategory(category);
                                  }}
                                  className="btn btn-icon btn-sm"
                                  title="Edit"
                                  style={{ padding: '4px' }}
                                >
                                  <Pencil size={16} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteCategory(category._id);
                                  }}
                                  className="btn btn-icon btn-sm"
                                  title="Delete"
                                  style={{ padding: '4px', color: '#DC2626' }}
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="contacts-pagination-controls">
                  {/* Records Per Page Selector */}
                  <div className="contacts-records-per-page">
                    <span>Show</span>
                    <select
                      value={categoryPagination.limit}
                      onChange={(e) => handleCategoryLimitChange(Number(e.target.value))}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span>records per page (Total: {categoryPagination.total})</span>
                  </div>

                  {/* Pagination */}
                  {categoryPagination.pages > 1 && (
                    <div className="contacts-pagination-wrapper">
                      <Pagination
                        current={categoryPagination.page}
                        total={categoryPagination.pages}
                        onPageChange={handleCategoryPageChange}
                        maxWidth={400}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
          </div>
        </div>

        {/* Option Form Drawer (Create Only) */}
        <Drawer
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          title="Add New Option"
          footer={
            <div className="drawer-footer-actions">
              <button
                type="button"
                className="btn btn-secondary btn-md"
                onClick={handleCloseDrawer}
                disabled={formLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="option-form"
                className="btn btn-primary btn-md"
                disabled={formLoading}
              >
                {formLoading ? 'Creating...' : 'Create'}
              </button>
            </div>
          }
        >
          {isDrawerOpen && workspaceId && (
            <OptionFormSlider
              workspaceId={workspaceId}
              projectId={projectFilter || undefined}
              onSuccess={handleCloseDrawer}
              onCancel={handleCloseDrawer}
              onLoadingChange={setFormLoading}
            />
          )}
        </Drawer>

        {/* Option Details Slider */}
        {viewOptionId && (
          <OptionDetailsSlider
            workspaceId={workspaceId}
            optionId={viewOptionId}
            onClose={handleCloseSlider}
            onSave={handleSaveOption}
          />
        )}

        {/* Add/Edit Category Modal */}
        {showAddCategoryModal && (
          <AddCategoryModal
            workspaceId={workspaceId}
            category={editingCategory || undefined}
            onClose={handleCategoryModalClose}
            onSave={handleCategoryModalSave}
          />
        )}
      </>
    </AppLayout>
  );
};

export default Options;
